import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { SEO } from '../components/shared/SEO';
import { Header } from '../components/layout/Header';
import { AnnouncementBar } from '../components/layout/AnnouncementBar';
import { MobileDeliverToPill } from '../components/layout/MobileDeliverToPill';
import { Footer } from '../components/layout/Footer';
import { MobileFooterNav } from '../components/layout/MobileFooterNav';
import { PharmacistChat } from '../components/services/PharmacistChat';
import { PageHeroBreadcrumb } from '../components/layout/PageHeroBreadcrumb';
import { CategoryCard } from '../components/shared/CategoryCard';
import { ChevronRightIcon } from 'lucide-react';
import { MobileInternalHeader } from '../components/layout/MobileInternalHeader';
import { HomeIcon } from 'lucide-react';
export function CategoriesPage() {
  const navigate = useNavigate();
  const [locationModalOpen, setLocationModalOpen] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<{
    name: string;
  } | null>(null);
  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const categories = [{
    id: 'medicines',
    name: 'Medicines & Treatments',
    icon: '💊',
    color: 'from-red-500 to-red-600',
    bgColor: 'bg-red-50',
    textColor: 'text-red-600',
    productCount: 850,
    subcategories: ['Pain Relief', 'Cold & Flu', 'Allergies', 'Digestive Health', 'Sleep Aids'],
    image: 'https://images.unsplash.com/photo-1584308666744-24d5c474f2ae?w=400&h=400&fit=crop'
  }, {
    id: 'vitamins',
    name: 'Vitamins & Supplements',
    icon: '💪',
    color: 'from-green-500 to-green-600',
    bgColor: 'bg-green-50',
    textColor: 'text-green-600',
    productCount: 650,
    subcategories: ['Multivitamins', 'Vitamin D', 'Omega-3', 'Probiotics', 'Protein Powder'],
    image: 'https://images.unsplash.com/photo-1550572017-4bfa2d5c8f1d?w=400&h=400&fit=crop'
  }, {
    id: 'medical-devices',
    name: 'Medical Devices',
    icon: '🩺',
    color: 'from-blue-500 to-blue-600',
    bgColor: 'bg-blue-50',
    textColor: 'text-blue-600',
    productCount: 320,
    subcategories: ['Blood Pressure Monitors', 'Glucose Meters', 'Thermometers', 'Nebulizers', 'Pulse Oximeters'],
    image: 'https://images.unsplash.com/photo-1631549916768-4119b2e5f926?w=400&h=400&fit=crop'
  }, {
    id: 'beauty',
    name: 'Beauty & Skin Care',
    icon: '✨',
    color: 'from-pink-500 to-pink-600',
    bgColor: 'bg-pink-50',
    textColor: 'text-pink-600',
    productCount: 780,
    subcategories: ['Facial Care', 'Hair Care', 'Body Care', 'Sun Protection', 'Anti-Aging'],
    image: 'https://images.unsplash.com/photo-1556228720-195a672e8a03?w=400&h=400&fit=crop'
  }, {
    id: 'baby-care',
    name: 'Baby & Mother Care',
    icon: '👶',
    color: 'from-purple-500 to-purple-600',
    bgColor: 'bg-purple-50',
    textColor: 'text-purple-600',
    productCount: 520,
    subcategories: ['Baby Formula', 'Diapers', 'Baby Food', 'Prenatal Vitamins', 'Nursing Supplies'],
    image: 'https://images.unsplash.com/photo-1515488042361-ee00e0ddd4e4?w=400&h=400&fit=crop'
  }, {
    id: 'personal-care',
    name: 'Personal Care',
    icon: '🧴',
    color: 'from-teal-500 to-teal-600',
    bgColor: 'bg-teal-50',
    textColor: 'text-teal-600',
    productCount: 430,
    subcategories: ['Oral Care', 'Bath & Shower', 'Deodorants', 'Shaving', 'Hand Care'],
    image: 'https://images.unsplash.com/photo-1556228852-80f59c1bb3cf?w=400&h=400&fit=crop'
  }, {
    id: 'health-monitoring',
    name: 'Health Monitoring',
    icon: '📊',
    color: 'from-indigo-500 to-indigo-600',
    bgColor: 'bg-indigo-50',
    textColor: 'text-indigo-600',
    productCount: 280,
    subcategories: ['Fitness Trackers', 'Smart Scales', 'Heart Rate Monitors', 'Sleep Trackers', 'Health Apps'],
    image: 'https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=400&h=400&fit=crop'
  }, {
    id: 'herbal',
    name: 'Herbal & Natural',
    icon: '🌿',
    color: 'from-lime-500 to-lime-600',
    bgColor: 'bg-lime-50',
    textColor: 'text-lime-600',
    productCount: 390,
    subcategories: ['Green Tea', 'Ginseng', 'Turmeric', 'Essential Oils', 'Herbal Teas'],
    image: 'https://images.unsplash.com/photo-1471864190281-a93a3070b6de?w=400&h=400&fit=crop'
  }, {
    id: 'first-aid',
    name: 'First Aid & Safety',
    icon: '🚑',
    color: 'from-orange-500 to-orange-600',
    bgColor: 'bg-orange-50',
    textColor: 'text-orange-600',
    productCount: 210,
    subcategories: ['First Aid Kits', 'Bandages', 'Antiseptics', 'Safety Equipment', 'Emergency Supplies'],
    image: 'https://images.unsplash.com/photo-1603398938378-e54eab446dde?w=400&h=400&fit=crop'
  }];
  return <HelmetProvider data-id="element-6681" data-mp="1ogq2j5pvzt1m">
      <SEO title="All Categories" description="Explore all Innova Pharmacy categories — health, beauty, mom & baby and more. Shop online with fast delivery across Saudi Arabia, secure checkout, great prices." canonical="/en/categories/" ogImage="https://cdn.innovaksa.net/og/categories.jpg" hreflangAr="/categories/" hreflangEn="/en/categories/" hreflangDefault="/categories/" data-id="element-6682" data-mp="1oiaviqwjz3xd" />
      <div className="flex flex-col min-h-screen bg-gray-50" data-id="element-6683" data-mp="5h0alaaj4xlh">
        {/* Desktop Header */}
        <div className="hidden md:block" data-id="element-6684" data-mp="ah4r30bftsbp">
          <AnnouncementBar data-id="element-6685" data-mp="ag3gsy28x9v1" />
          <MobileDeliverToPill selectedLocation={selectedLocation} onOpenLocationModal={() => setLocationModalOpen(true)} data-id="element-6686" data-mp="aifswulboq06" />
          <Header locationModalOpen={locationModalOpen} setLocationModalOpen={setLocationModalOpen} selectedLocation={selectedLocation} setSelectedLocation={setSelectedLocation} data-id="element-6687" data-mp="piht9inu3bl" />
        </div>
        <main className="flex-grow pt-14 md:pt-[var(--header-height-desktop)]" data-id="element-6688" data-mp="38y8q3nhsbld1">
          {/* Mobile Header Overlay - Only on mobile */}
          <div className="md:hidden fixed top-0 left-0 right-0 z-50" data-id="element-6689" data-mp="3dyeix6zsjdwp">
            <MobileInternalHeader title="All Categories" data-id="element-6690" data-mp="1775eow02m2rf" />
          </div>
          <h1 className="sr-only" data-id="element-6691" data-mp="1752oaav0a7m6">
            Shop by Category - Complete Healthcare Product Range
          </h1>
          {/* Page Hero Breadcrumb */}
          <PageHeroBreadcrumb breadcrumbs={[{
          label: 'Home',
          href: '/'
        }, {
          label: 'All Categories'
        }]} title="All Categories" description="Explore all Innova Pharmacy categories — health, beauty, mom & baby and more. Shop online with fast delivery across Saudi Arabia." data-id="element-6692" data-mp="2wp2gjdk5y19b" />
          {/* Categories Grid */}
          <div className="container mx-auto px-4 py-8" data-id="element-6693" data-mp="3afxjk97zdo4z">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-6694" data-mp="3ahifa2617d4f">
              {categories.map(category => <CategoryCard key={category.id} category={category} data-id="element-6695" data-mp="3kdv8kyxnzoyn" />)}
            </div>
          </div>
        </main>
        <div className="hidden md:block" data-id="element-6696" data-mp="1uzoz2d68rjhi">
          <Footer data-id="element-6697" data-mp="1v0ib9tnyz171" />
        </div>
        <MobileFooterNav data-id="element-6698" data-mp="1q03zq1n5t949" />
        <PharmacistChat data-id="element-6699" data-mp="1q0vwhfshtcw4" />
        <div className="h-16 md:hidden" data-id="element-6700" data-mp="1q1nhxxsdlu8o"></div>
      </div>
    </HelmetProvider>;
}